<script>
import FilterPanel from '@/components/common/FilterPanel.vue';
import FilterField from '@/components/common/FilterField.vue';
import MultiSelectFilter from '@/components/common/MultiSelectFilter.vue';
import DateRangeFilter from '@/components/common/DateRangeFilter.vue';
import SavedFilters from '@/components/common/SavedFilters.vue';
import { SAVED_FILTER_SCOPES } from '@/shared/services/saved-filter.service';

/**
 * Les critères de « Mes enquêtes ».
 *
 * Le statut reste un choix unique: ce sont les trois cartes du récapitulatif
 * qui le posent, et un choix multiple les contredirait. Le reste — gouvernorat,
 * secteur, exercice, dates — s'ouvre dans le panneau avancé, peuplé par les
 * seules valeurs que les enquêtes du compte contiennent.
 */
export default {
  name: 'ProSurveyFilters',
  components: {
    FilterPanel,
    FilterField,
    MultiSelectFilter,
    DateRangeFilter,
    SavedFilters,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    /** { governorates, sectors, referenceYears } */
    options: {
      type: Object,
      default: () => ({ governorates: [], sectors: [], referenceYears: [] }),
    },
    activeCount: { type: Number, default: 0 },
    criteria: { type: Object, default: () => ({}) },
    appliedFilterId: { type: String, default: null },
    expanded: { type: Boolean, default: false },
  },
  emits: [
    'filter-change',
    'update:expanded',
    'reset',
    'apply-saved',
    'saved-filters-loaded',
  ],
  data() {
    return {
      scope: SAVED_FILTER_SCOPES.PRO_SURVEYS,
      respondentOptions: [
        { value: '', label: 'Peu importe' },
        { value: 'yes', label: 'Renseigné' },
        { value: 'no', label: 'Non renseigné' },
      ],
    };
  },
  computed: {
    governorateChoices() {
      return (this.options.governorates || []).map((value) => ({
        value,
        label: value,
      }));
    },

    sectorChoices() {
      return (this.options.sectors || []).map((value) => ({
        value,
        label: value,
      }));
    },

    yearChoices() {
      return (this.options.referenceYears || []).map((year) => ({
        value: year,
        label: String(year),
      }));
    },
  },
  methods: {
    update(field, value) {
      this.$emit('filter-change', { ...this.filters, [field]: value });
    },
  },
};
</script>

<template>
  <FilterPanel
    :model-value="filters.searchQuery"
    :expanded="expanded"
    :active-count="activeCount"
    search-placeholder="Rechercher une entreprise…"
    @update:model-value="update('searchQuery', $event)"
    @update:expanded="$emit('update:expanded', $event)"
    @reset="$emit('reset')"
  >
    <template #quick>
      <select
        class="list-select"
        :value="filters.status"
        aria-label="Filtrer par statut"
        data-testid="pro-status"
        @change="update('status', $event.target.value)"
      >
        <option value="ALL">Statut : tous</option>
        <option value="SUBMITTED">Soumise</option>
        <option value="DRAFT">Brouillon</option>
      </select>

      <select
        class="list-select"
        :value="filters.perPage"
        aria-label="Nombre de lignes par page"
        @change="update('perPage', Number($event.target.value))"
      >
        <option :value="8">8 par page</option>
        <option :value="10">10 par page</option>
        <option :value="25">25 par page</option>
        <option :value="50">50 par page</option>
      </select>
    </template>

    <template #saved>
      <SavedFilters
        :scope="scope"
        :criteria="criteria"
        :applied-id="appliedFilterId"
        @apply="$emit('apply-saved', $event)"
        @loaded="$emit('saved-filters-loaded', $event)"
      />
    </template>

    <template #advanced>
      <FilterField label="Gouvernorats">
        <MultiSelectFilter
          :model-value="filters.governorates"
          :options="governorateChoices"
          placeholder="Tous les gouvernorats"
          @update:model-value="update('governorates', $event)"
        />
      </FilterField>

      <FilterField label="Secteurs d'activité">
        <MultiSelectFilter
          :model-value="filters.sectors"
          :options="sectorChoices"
          placeholder="Tous les secteurs"
          @update:model-value="update('sectors', $event)"
        />
      </FilterField>

      <FilterField label="Exercices">
        <MultiSelectFilter
          :model-value="filters.referenceYears"
          :options="yearChoices"
          placeholder="Tous les exercices"
          @update:model-value="update('referenceYears', $event)"
        />
      </FilterField>

      <FilterField label="Nom du répondant">
        <select
          class="list-select"
          :value="filters.hasRespondent"
          data-testid="pro-has-respondent"
          @change="update('hasRespondent', $event.target.value)"
        >
          <option
            v-for="option in respondentOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </FilterField>

      <FilterField label="Date d'entretien" wide>
        <DateRangeFilter
          :model-value="filters.interviewDate"
          @update:model-value="update('interviewDate', $event)"
        />
      </FilterField>

      <FilterField label="Date de création" wide>
        <DateRangeFilter
          :model-value="filters.dateAdded"
          @update:model-value="update('dateAdded', $event)"
        />
      </FilterField>
    </template>
  </FilterPanel>
</template>
